import { S3Client } from '@aws-sdk/client-s3';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { Upload } from '@aws-sdk/lib-storage';
import { AwsCredentialIdentity, Provider } from '@aws-sdk/types';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';
import { format } from 'date-fns';
import { generate } from 'shortid';
import { DivClassType } from 'types/divClassType';

export class AppHelper {
  /* ----------------------------------------------------------------------- */
  /* Validation関連 */
  /* ----------------------------------------------------------------------- */
  /**
   * 値の存在検証をします。
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static isEmpty(input: any): boolean {
    if (input === null || input === undefined) {
      return true;
    } else if (input === '') {
      return true;
    } else if (Array.isArray(input) && input.length === 0) {
      return true;
    } else if (typeof input === 'object' && Object.keys(input).length === 0) {
      return true;
    }
    return false;
  }

  /**
   * 値の存在検証をします。
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static isNotEmpty(input: any): boolean {
    return !this.isEmpty(input);
  }

  /**
   * URLフォーマットの検証をします。
   */
  static isUrl(input: string) {
    let url;
    try {
      url = new URL(input);
    } catch (_) {
      return false;
    }
    return url.protocol === 'http:' || url.protocol === 'https:';
  }

  /* ----------------------------------------------------------------------- */
  /* 区分定数関連 */
  /* ----------------------------------------------------------------------- */
  /**
   * 区分値のラベルを取得します。
   */
  static getDivLabel(divClass: DivClassType, value: number): string {
    const entry = Object.values(DivConst.items[divClass]).find(item => item.value === value);
    return entry?.label ?? '';
  }
  /**
   * 区分値の存在検証をします。
   */
  static isDivValue(divClass: DivClassType, value: number): boolean {
    return Object.values(DivConst.items[divClass]).some(v => v.value === value);
  }

  /**
   * 選択オプション用の区分値の配列を返します。
   */
  static getDivSelectOptions(divClass: DivClassType): { value: string; label: string }[] {
    return Object.values(DivConst.items[divClass]).map(item => ({
      value: item.value.toString(),
      label: item.label,
    }));
  }

  /* ----------------------------------------------------------------------- */
  /* 画面制御関連 */
  /* ----------------------------------------------------------------------- */
  /**
   * 二重送信を防止します。
   */
  static async submit(
    processing: boolean,
    setProcessing: (processing: boolean) => void,
    callback: { (): Promise<void> }
  ): Promise<void> {
    if (processing) {
      return;
    }
    setProcessing(true);
    await callback();
    setProcessing(false);
    return;
  }

  /* ----------------------------------------------------------------------- */
  /* 画面表示関連 */
  /* ----------------------------------------------------------------------- */
  /**
   * カンパニー画像のURLを取得します。
   */
  static getCompanyImageUrl(imageKey: string): string {
    if (imageKey) {
      return this._convS3KeyToUrl(imageKey);
    }
    return `${config.s3.bucket.staticContents.endpoint}/sys/default/company.png`;
  }
  /**
   * クイズ画像のURLを取得します。
   */
  static getContentImageUrl(imageKey: string): string {
    if (imageKey) {
      return this._convS3KeyToUrl(imageKey);
    }
    return `${config.s3.bucket.staticContents.endpoint}/sys/default/quiz.jpg`;
  }

  /**
   * S3のパスをCDNのURLに変換します。
   */
  static _convS3KeyToUrl(imageKey: string | null): string {
    if (!imageKey) {
      return null;
    }
    return `${config.s3.bucket.staticContents.endpoint}${imageKey.replace('public/', '/')}`;
  }

  /**
   * S3にBlob形式のファイルをアップロードします。
   */
  static async uploadS3(blob: Blob, extension: string): Promise<string> {
    if (!blob) {
      return null;
    }
    // ファイル名の設定
    const filePath = `${this.getUid(28)}.${extension}`;
    // S3オブジェクト作成
    const s3Client = new S3Client({
      credentials: await this.getAwsCredential(),
      region: config.cognito.region,
    });
    // ファイルのアップロード
    const upload = new Upload({
      client: s3Client,
      params: {
        Bucket: config.s3.bucket.upload.name,
        Key: filePath,
        Body: blob,
      },
    });
    upload.on('httpUploadProgress', progress => {
      console.debug(progress);
    });
    await upload.done();

    return filePath;
  }

  /**
   * AWSの認証情報を設定します。
   */
  static async getAwsCredential(): Promise<Provider<AwsCredentialIdentity>> {
    // 人情情報を取得
    const session = await fetchAuthSession();

    // PROVIDER_KEY作成
    const providerKey =
      'cognito-idp.' + config.cognito.region + '.amazonaws.com/' + config.cognito.userPoolId;

    // IDプールにてIDトークンの認証を行う
    return fromCognitoIdentityPool({
      clientConfig: { region: config.cognito.region },
      identityPoolId: config.cognito.identityPoolId,
      logins: {
        [providerKey]: session.tokens.idToken.toString(),
      },
    });
  }

  /* ----------------------------------------------------------------------- */
  /* 画面フォーマット関連 */
  /* ----------------------------------------------------------------------- */
  /**
   * 数値を3桁ごとにカンマ区切りにします。
   */
  static formatNumberToCurrency(input: number): string {
    if (input == null) {
      return null;
    }
    return input.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * 割合をパーセント表示にします。
   * 第二引数が指定された場合は。第一引数と第二引数を除算した値のパーセント表示にします。
   */
  static formatRateToPercent(numerator: number, denominator?: number): number {
    if (numerator == null) {
      return null;
    }
    // 第二引数が指定された場合は。第一引数と第二引数を除算した値のパーセント表示にします。
    const denominatorVal = denominator ?? 1;
    const rate = numerator && denominatorVal ? numerator / denominatorVal : 0;

    // 小数点以下を四捨五入して整数にする
    return Math.round(rate * 100);
  }

  /**
   *　ミリ秒を日付フォーマットに変換します。
   */
  static formatTimeToDate(milliseconds: number, dateFormat: string): string {
    if (milliseconds == null) {
      return null;
    }
    const date = new Date(milliseconds);
    return format(date, dateFormat);
  }

  /**
   *　バイト数をフォーマットに変換します。
   */
  static formatBytes(bytes: number): string {
    if (bytes == null) {
      return null;
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let i = 0;

    while (bytes >= 1024 && i < units.length - 1) {
      bytes /= 1024;
      i++;
    }

    return `${bytes.toFixed(2)} ${units[i]}`;
  }

  /* ----------------------------------------------------------------------- */
  /* 共通ユーティリティ */
  /* ----------------------------------------------------------------------- */
  /**
   * UIDを発行します。
   */
  static getUid = (digits: number): string => {
    let uuid = '';
    while (uuid.length < digits) {
      uuid += generate();
    }
    return uuid.substring(0, digits);
  };

  // /**
  //  * UIDを発行します。
  //  */
  // static getUid = (digits: number): string => {
  //   let uuid = "";
  //   while (uuid.length < digits) {
  //     uuid += generate();
  //   }
  //   return uuid.substring(0, digits);
  // };

  // /**
  //  * ファイル名から拡張子を取得します。
  //  */
  // static getFileExtension = (filename: string): string | null => {
  //   const extensionIndex = filename.lastIndexOf(".");
  //   if (extensionIndex === -1) {
  //     return null;
  //   }
  //   return filename.slice(extensionIndex + 1);
  // };

  // /**
  //  * URLからドメインを取得します。
  //  */
  // static getDomainFromUrl = (url: string): string | null => {
  //   const domainIndex = url.indexOf("//") + 2;
  //   if (domainIndex === -1) {
  //     return null;
  //   }
  //   const domain = url.slice(domainIndex);
  //   return domain.split("/")[0];
  // };

  // /**
  //  * 配列の重複を除去します。
  //  */
  // static removeDuplications(items: any[]): any[] {
  //   const set = new Set(items);
  //   return [...set];
  // }
}
